import React, { useEffect, useState } from 'react';
import { dispatcher } from 'dispatcher';
import { prop } from 'ramda';

import { Icon } from '@core/js/components/common/Icon';

import { Logo } from '@interlayer/js/app/components/common/Logo';
import { SvgIcon } from '@interlayer/js/app/components/common/SvgIcon';
import { WidgetAccount } from '@interlayer/js/app/components/common/WidgetAccount';
import { GamesSearchGlobal } from '@interlayer/js/app/components/main/GameSearchGlobal';
import { Button } from '@interlayer/js/components/common/Button';

import { useTranslations } from '@core/js/hooks/i18n';
import { useAppSelector, useUserSelector } from '@core/js/store/hooks';

import { useSwitchMobileMenu } from '@interlayer/js/app/components/main/DefaultHeader/components/HeaderMobile/hooks';
import {
    DELAY_GAME_SEARCH_INPUT_IN_FOCUS,
    GAME_SEARCH_HEADER_ID } from '@interlayer/js/app/components/main/GameSearchGlobal/constants';
import { useInDesktop } from '@interlayer/js/app/hooks/device/hooks/useMediaQuery';
import { useConfigTag } from '@interlayer/js/app/hooks/useConfigTag';
import { clsx } from '@interlayer/js/app/utils/clsx.util';
import { EVENT_SHOW_LOGIN_POPUP } from '@interlayer/js/components/popups/LoginPopup/buzz/constants';
import { useLoginPopup } from '@interlayer/js/components/popups/LoginPopup/hooks';
import { CONFIG_TAG_TYPE } from '@interlayer/js/components/system/TagManager/buzz/constants';
import { TJoinNowConfigTagConfig } from '@interlayer/js/components/system/TagManager/buzz/types';

import style from './HeaderMobile.scss';

export const HeaderMobile: React.FC = () => {
    const showLoginPopup = useLoginPopup();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const rootPage = useAppSelector(prop('rootPage'));
    const switchMobileMenu = useSwitchMobileMenu();
    const isDesktop = useInDesktop();
    const t = useTranslations('playtech.mobile.system.header.');
    const [isSearchShow, setSearchShow] = useState(false);
    const joinNowConfig = useConfigTag(CONFIG_TAG_TYPE.joinNowConfig) as TJoinNowConfigTagConfig;

    useEffect(() => {
        const closeSearchBlock = () => setSearchShow(false);

        dispatcher.listen(EVENT_SHOW_LOGIN_POPUP, closeSearchBlock);

        return () => {
            dispatcher.removeListener(EVENT_SHOW_LOGIN_POPUP, closeSearchBlock);
        };
    }, []);

    const handleIconClick = (status: boolean) => {
        if (status) {
            setTimeout(() => {
                document.getElementById(GAME_SEARCH_HEADER_ID)?.focus();
            }, DELAY_GAME_SEARCH_INPUT_IN_FOCUS);
        }

        setSearchShow(status);
    };

    return (
        <div className={clsx(style.header, { [style.isSearchShow]: isSearchShow })}>
            <div className={style.left}>
                <div className={style.hamburger} onClick={() => switchMobileMenu()}>
                    <Icon className={style.hamburgerIcon} type="menu_hamburger" />
                </div>

                <Logo
                    href={rootPage}
                    className={style.logo}
                    size="44px"
                />
            </div>

            <div className={style.right}>
                {isLoggedIn
                    ? <WidgetAccount setSearchShow={setSearchShow} isSearchShow={isSearchShow} />
                    : (
                        <>
                            {joinNowConfig?.config?.isEnabled && (
                                <Button
                                    size="small"
                                    href={joinNowConfig.config.url}
                                    view="secondary"
                                    className={style.btn}
                                >
                                    {joinNowConfig.config.text}
                                </Button>
                            )}

                            <Button
                                size="small"
                                className={style.btn}
                                onClick={showLoginPopup}
                            >
                                {t('login')}
                            </Button>

                            {!isDesktop && (
                                <div
                                    onClick={() => handleIconClick(true)}
                                    className={style.account}
                                >
                                    <SvgIcon
                                        className={style.icon}
                                        type="search-thin"
                                    />
                                </div>
                            )}

                            {isSearchShow && (
                                <div className={style.searchBlock}>
                                    <GamesSearchGlobal className={style.wrap} />

                                    <SvgIcon
                                        onClick={() => handleIconClick(false)}
                                        className={style.close}
                                        type="close-sidebar"
                                    />
                                </div>
                            )}
                        </>
                    )
                }
            </div>
        </div>
    );
};
